import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveModule, toggleTheme } from '@store/slices/uiSlice';
import {
    Wallet, HardHat, ArrowRight, Building2, BarChart2,
    Package, Users, Sun, Moon, Receipt, TrendingUp,
    ClipboardCheck, Boxes
} from 'lucide-react';
import clsx from 'clsx';

export default function KarsilamaSayfasi() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { theme } = useSelector((state) => state.ui);
    const isDark = theme === 'dark';

    const handleModuleSelect = (module) => {
        dispatch(setActiveModule(module));
        navigate(module === 'finans' ? '/finans/ozet' : '/insaat/ozet');
    };

    return (
        <div className={clsx(
            'min-h-screen relative overflow-hidden transition-colors duration-500',
            isDark
                ? 'bg-gradient-to-br from-primary-900 via-primary-dark to-primary'
                : 'bg-gradient-to-br from-neutral-50 via-white to-primary-50'
        )}>
            {/* Arka Plan Deseni */}
            <div className={clsx(
                'absolute inset-0',
                isDark ? 'opacity-[0.03]' : 'opacity-[0.04]'
            )}>
                <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, ${isDark ? 'white' : '#334155'} 1px, transparent 0)`,
                    backgroundSize: '40px 40px'
                }} />
            </div>

            {/* Üst Glow Efekti */}
            <div className={clsx(
                'absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px]',
                isDark ? 'bg-secondary/10' : 'bg-secondary/5'
            )} />

            {/* Alt Glow */}
            <div className={clsx(
                'absolute bottom-0 right-0 w-[600px] h-[300px] rounded-full blur-[100px]',
                isDark ? 'bg-primary-400/5' : 'bg-primary/3'
            )} />

            {/* Tema Değiştirme Butonu - Sağ Üst */}
            <button
                onClick={() => dispatch(toggleTheme())}
                className={clsx(
                    'fixed top-6 right-6 z-50 p-3 rounded-xl border transition-all duration-300 cursor-pointer',
                    'hover:scale-110 active:scale-95',
                    isDark
                        ? 'bg-white/10 border-white/10 text-primary-200 hover:bg-white/20 hover:text-white'
                        : 'bg-white border-border-light text-text-secondary hover:bg-neutral-50 hover:text-primary shadow-sm hover:shadow-md'
                )}
                title={isDark ? 'Açık Tema' : 'Koyu Tema'}
            >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* İçerik */}
            <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">

                {/* Logo ve Başlık */}
                <div className="text-center mb-12 md:mb-16">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <img src="/logo.png" alt="İNŞA ET" className="h-14 w-14 md:h-16 md:w-16 object-contain drop-shadow-2xl" />
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                            <span className={isDark ? 'text-white' : 'text-primary-dark'}>İNŞA</span>
                            <span className="text-secondary"> ET</span>
                        </h1>
                    </div>
                    <p className={clsx(
                        'text-lg md:text-xl font-light max-w-lg mx-auto leading-relaxed',
                        isDark ? 'text-primary-300' : 'text-text-tertiary'
                    )}>
                        İnşaat projelerinizi tek platformdan yönetin
                    </p>
                </div>

                {/* Hoş Geldin */}
                {user?.name && (
                    <p className={clsx(
                        'text-sm mb-8 font-medium',
                        isDark ? 'text-primary-400' : 'text-text-muted'
                    )}>
                        Hoş geldin, <span className={isDark ? 'text-white' : 'text-primary-dark font-semibold'}>{user.name}</span>
                    </p>
                )}

                {/* Modül Kartları */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 w-full max-w-4xl">

                    {/* Finans Modülü Kartı */}
                    <button
                        onClick={() => handleModuleSelect('finans')}
                        className={clsx(
                            'group relative rounded-2xl p-8 text-left transition-all duration-500 hover:-translate-y-1 cursor-pointer',
                            isDark
                                ? 'bg-white/[0.06] backdrop-blur-xl border border-primary-400/20 hover:bg-white/[0.1] hover:border-primary-400/40 hover:shadow-2xl hover:shadow-primary-400/10'
                                : 'bg-white border border-primary/20 shadow-sm hover:border-primary/40 hover:shadow-xl hover:shadow-primary/8'
                        )}
                    >
                        {/* Üst gradient çizgi */}
                        <div className={clsx(
                            'absolute top-0 left-6 right-6 h-[2px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500',
                            'bg-gradient-to-r from-transparent via-primary to-transparent'
                        )} />

                        {/* İkon */}
                        <div className={clsx(
                            'w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300',
                            isDark
                                ? 'bg-gradient-to-br from-primary-400/20 to-primary-500/10'
                                : 'bg-gradient-to-br from-primary/10 to-primary-100'
                        )}>
                            <Wallet size={28} className={clsx(
                                'transition-colors',
                                isDark
                                    ? 'text-primary-300 group-hover:text-white'
                                    : 'text-primary group-hover:text-primary-dark'
                            )} />
                        </div>

                        {/* Başlık */}
                        <h2 className={clsx(
                            'text-2xl font-bold mb-3 flex items-center gap-2',
                            isDark ? 'text-white' : 'text-primary-dark'
                        )}>
                            Finans Yönetimi
                            <ArrowRight size={20} className={clsx(
                                'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300',
                                isDark ? 'text-primary-300' : 'text-primary'
                            )} />
                        </h2>

                        {/* Açıklama */}
                        <p className={clsx(
                            'text-sm leading-relaxed mb-6',
                            isDark ? 'text-primary-300' : 'text-text-secondary'
                        )}>
                            Nakit akışı, satışlar, taksitler, harcamalar ve mali raporları tek ekrandan takip edin. Gelir-gider dengesini anlık izleyin.
                        </p>

                        {/* Özellik etiketleri */}
                        <div className="flex flex-wrap gap-2">
                            {[
                                { icon: BarChart2, label: 'Mali Raporlar' },
                                { icon: Building2, label: 'Proje Takibi' },
                                { icon: Wallet, label: 'Nakit Akışı' },
                                { icon: Users, label: 'Müşteri Yönetimi' },
                                { icon: Receipt, label: 'Taksit Takibi' },
                                { icon: TrendingUp, label: 'Gelir-Gider' },
                            ].map(({ icon: Icon, label }) => (
                                <span key={label} className={clsx(
                                    'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border',
                                    isDark
                                        ? 'text-primary-400 bg-white/5 border-white/5'
                                        : 'text-text-tertiary bg-neutral-50 border-border-light'
                                )}>
                                    <Icon size={12} />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </button>

                    {/* İnşaat Modülü Kartı */}
                    <button
                        onClick={() => handleModuleSelect('insaat')}
                        className={clsx(
                            'group relative rounded-2xl p-8 text-left transition-all duration-500 hover:-translate-y-1 cursor-pointer',
                            isDark
                                ? 'bg-white/[0.06] backdrop-blur-xl border border-secondary/20 hover:bg-white/[0.1] hover:border-secondary/40 hover:shadow-2xl hover:shadow-secondary/10'
                                : 'bg-white border border-secondary/25 shadow-sm hover:border-secondary/45 hover:shadow-xl hover:shadow-secondary/8'
                        )}
                    >
                        {/* Üst gradient çizgi */}
                        <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-secondary to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-full" />

                        {/* İkon */}
                        <div className={clsx(
                            'w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300',
                            isDark
                                ? 'bg-gradient-to-br from-secondary/20 to-secondary-dark/10'
                                : 'bg-gradient-to-br from-secondary/10 to-secondary-100'
                        )}>
                            <HardHat size={28} className={clsx(
                                'transition-colors',
                                isDark
                                    ? 'text-secondary-300 group-hover:text-secondary'
                                    : 'text-secondary group-hover:text-secondary-dark'
                            )} />
                        </div>

                        {/* Başlık */}
                        <h2 className={clsx(
                            'text-2xl font-bold mb-3 flex items-center gap-2',
                            isDark ? 'text-white' : 'text-primary-dark'
                        )}>
                            İnşaat Yönetimi
                            <ArrowRight size={20} className={clsx(
                                'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300',
                                isDark ? 'text-secondary-300' : 'text-secondary'
                            )} />
                        </h2>

                        {/* Açıklama */}
                        <p className={clsx(
                            'text-sm leading-relaxed mb-6',
                            isDark ? 'text-primary-300' : 'text-text-secondary'
                        )}>
                            Proje ilerlemesi, malzeme takibi, personel yönetimi ve şantiye operasyonlarını kontrol edin. Blok bazlı ilerlemeyi izleyin.
                        </p>

                        {/* Özellik etiketleri */}
                        <div className="flex flex-wrap gap-2">
                            {[
                                { icon: Building2, label: 'İlerleme Takibi' },
                                { icon: Package, label: 'Malzeme Takibi' },
                                { icon: ClipboardCheck, label: 'Puantaj' },
                                { icon: Boxes, label: 'Stok Yönetimi' },
                                { icon: Users, label: 'Personel' },
                                { icon: HardHat, label: 'Şantiye' },
                            ].map(({ icon: Icon, label }) => (
                                <span key={label} className={clsx(
                                    'flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border',
                                    isDark
                                        ? 'text-primary-400 bg-white/5 border-white/5'
                                        : 'text-text-tertiary bg-neutral-50 border-border-light'
                                )}>
                                    <Icon size={12} />
                                    {label}
                                </span>
                            ))}
                        </div>
                    </button>
                </div>

                {/* Alt bilgi */}
                <p className={clsx(
                    'text-xs mt-12 tracking-wide',
                    isDark ? 'text-primary-500' : 'text-text-muted'
                )}>
                    © 2026 İNŞA ET — Bütünleşik İnşaat Yönetim Platformu
                </p>
            </div>
        </div>
    );
}
